import { styled } from '@linaria/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { GraphData } from '../../api/process-client/process-client.type';
import type { ResolvedPhaseLabels } from '../../helpers/phase-labels/phase-labels.type';
import { PhaseKey } from '../../helpers/phase-labels/phase-labels.type';
import { skyDark, skyLight, skyTight } from '../../styles/design-tokens';
import { Text } from '../../ui-library/typography/typography';

type Props = {
  graphData: GraphData | undefined;
  /**
   * Optional per-workflow display labels. When supplied, the
   * "Waiting" overview tile renders the override for the canonical
   * `blocked` key.
   */
  resolvedLabels?: ResolvedPhaseLabels;
};

interface ProcessOverviewTileProps {
  title: string;
  description: string;
  value: string | ReactNode;
}

export const ProcessOverviewTile = ({ title, description, value }: ProcessOverviewTileProps) => {
  return (
    <Tile>
      <Text weight="medium">{title}</Text>
      <Text color={skyDark} size="small" style={{ textAlign: 'center' }}>
        {description}
      </Text>
      {typeof value === 'string' ? (
        <Text size="large" weight="bold" style={{ marginTop: '16px' }}>
          {value}
        </Text>
      ) : (
        <div style={{ marginTop: '16px' }}>{value}</div>
      )}
    </Tile>
  );
};

export const ProcessOverview = ({ graphData, resolvedLabels }: Props) => {
  const i18n = useTranslation();
  const workUnitsName = i18n.t('common.work_units');
  const workUnitsNameLower = workUnitsName.toLowerCase();
  const waitingLabel = i18n.t('common.workflow_steps.waiting');
  const blockedLabel = resolvedLabels?.[PhaseKey.Blocked]?.trim() || waitingLabel;
  return (
    <TileContainer>
      <ProcessOverviewTile
        title={`Total ${workUnitsName}`}
        description={`Total number of ${workUnitsNameLower} considered`}
        value={`${graphData?.task_count || 0}`}
      />
      <ProcessOverviewTile
        title="Out of Workflow"
        description={`% of ${workUnitsNameLower} that skipped a workflow step`}
        value={`${graphData?.metrics?.skipped_tasks || 0}%`}
      />
      <ProcessOverviewTile
        title="Backwards Steps"
        description={`% of ${workUnitsNameLower} that went backwards`}
        value={`${graphData?.metrics?.backwards_tasks || 0}%`}
      />
      <ProcessOverviewTile
        title={blockedLabel}
        description={`% of ${workUnitsNameLower} that enter a ${blockedLabel.toLowerCase()} status`}
        value={`${graphData?.metrics?.blocked_tasks || 0}%`}
      />
    </TileContainer>
  );
};

const TileContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${skyLight};
  border-radius: 16px;
  padding: 16px;
  background-color: ${skyTight};
  flex: 1 1 0;
  min-width: 200px;
  box-sizing: border-box;
`;
